import { JSX } from "react";

export interface FilterOption {
  id: string;
  name: string;
}

export interface BaseFilterProps {
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  showBadges?: boolean;
  showRemoveAllButton?: boolean;
  disabled?: boolean;
}

export interface FilterChangeHandlers {
  [key: string]: (values: string[]) => void;
}

export interface FilterData {
  options: FilterOption[];
  isLoading: boolean;
  error?: Error | null;
}

export interface FilterPaginationState {
  hasMore: boolean;
  remainingCount: number;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onSearchChange: (value: string) => void;
}

export interface FilterLabelOverrides {
  labelSingular?: string;
  labelPlural?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
}

export abstract class Filter<TBody, TParams> {
  abstract readonly id: string;
  abstract readonly labelSingular: string;
  abstract readonly labelPlural: string;
  abstract readonly placeholder: string;
  abstract readonly searchPlaceholder: string;
  abstract readonly emptyText: string;
  group?: string;
  body: TBody;
  parameters: TParams;
  labelOverrides?: FilterLabelOverrides;

  setGroup(group: string): this {
    this.group = group;
    return this;
  }

  setLabelOverrides(overrides: FilterLabelOverrides): this {
    this.labelOverrides = overrides;
    return this;
  }

  get effectiveLabelSingular(): string {
    return this.labelOverrides?.labelSingular ?? this.labelSingular;
  }

  get effectiveLabelPlural(): string {
    return this.labelOverrides?.labelPlural ?? this.labelPlural;
  }

  get effectivePlaceholder(): string {
    return this.labelOverrides?.placeholder ?? this.placeholder;
  }

  get effectiveSearchPlaceholder(): string {
    return this.labelOverrides?.searchPlaceholder ?? this.searchPlaceholder;
  }

  get effectiveEmptyText(): string {
    return this.labelOverrides?.emptyText ?? this.emptyText;
  }

  abstract getData(): FilterData;
  abstract render(props: BaseFilterProps): JSX.Element;
}
